/*
    Copyright 2020 The caver-js Authors
    This file is part of the caver-js library.

    The caver-js library is free software: you can redistribute it and/or modify
    it under the terms of the GNU Lesser General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    The caver-js library is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
    GNU Lesser General Public License for more details.

    You should have received a copy of the GNU Lesser General Public License
    along with the caver-js. If not, see <http://www.gnu.org/licenses/>.
*/

const _ = require('lodash')
const BigNumber = require('bignumber.js')
const { isBigNumber } = require('../../caver-utils')

async function determineSendParams(executableObj, sendParam, options) {
    let sendOptions = {}
    sendOptions = Object.assign(sendOptions, options)
    sendOptions = Object.assign(sendOptions, sendParam)
    const { from, gas, feeDelegation, feePayer, feeRatio } = sendOptions
    if (!from)
        throw new Error(
            `'from' is missing. Please pass the sender's address in sendParam.from or define default sender address at 'kctContract.options.from'.`
        )

    if (gas === undefined) {
        const estimated = await executableObj.estimateGas({ from })
        const originalGas = new BigNumber(estimated, 10)
        const bufferGas = new BigNumber(1.7, 10)

        sendOptions.gas = Math.round(originalGas.times(bufferGas))
    }

    if (feeDelegation === undefined && (feePayer || feeRatio !== undefined)) {
        throw new Error('To use fee delegation with KCT, please set `feeDelegation` field to true.')
    }

    return sendOptions
}

function formatParamForUint256(param) {
    return convertToNumberString(param)
}

function convertToNumberString(value) {
    if (!isBigNumber(value) && !_.isNumber(value) && !_.isString(value)) throw new Error(`unsupported type`)

    const bn = new BigNumber(value)
    const numberString = bn.toString(10)

    if (numberString === 'NaN') throw new Error(`invalid parameter value`)

    return numberString
}

const errForDeployParamValidation = 'Failed to validate token info for deploy: '

function _validateCommonParam(obj) {
    if (!obj.name || !_.isString(obj.name)) throw new Error(`${errForDeployParamValidation}Invalid name of token`)
    if (!obj.symbol || !_.isString(obj.symbol)) throw new Error(`${errForDeployParamValidation}Invalid symbol of token`)
}

function validateDeployParameterForKIP7(obj) {
    _validateCommonParam(obj)

    if (obj.decimals === undefined || !_.isNumber(obj.decimals)) throw new Error(`${errForDeployParamValidation}Invalid decimals of token`)

    try {
        if (obj.initialSupply === undefined) {
            throw new Error(`Invalid initialSupply of token: ${obj.initialSupply}`)
        } else {
            obj.initialSupply = convertToNumberString(obj.initialSupply)
        }
    } catch (e) {
        // Catch the error here to add more details to the error message.
        throw new Error(`${errForDeployParamValidation}${e.message}`)
    }
}

function validateDeployParameterForKIP17(obj) {
    _validateCommonParam(obj)
}

function validateDeployParameterForKIP37(obj) {
    if (!obj.uri || !_.isString(obj.uri)) throw new Error(`${errForDeployParamValidation}Invalid uri of token`)
}

// KIP-7 token contract source code
// https://github.com/klaytn/klaytn-contracts/tree/master/contracts/token/KIP7/KIP7Token.sol
// The ABI and bytecode below are built via the following command.
// solc --abi --bin --allow-paths . klaytn-contracts/contracts/token/KIP7/KIP7Token.sol
const kip7JsonInterface = [
    {
        inputs: [
            { internalType: 'string', name: 'name', type: 'string' },
            { internalType: 'string', name: 'symbol', type: 'string' },
            { internalType: 'uint8', name: 'decimals', type: 'uint8' },
            { internalType: 'uint256', name: 'initialSupply', type: 'uint256' },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            { indexed: true, internalType: 'address', name: 'owner', type: 'address' },
            { indexed: true, internalType: 'address', name: 'spender', type: 'address' },
            { indexed: false, internalType: 'uint256', name: 'value', type: 'uint256' },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: true, internalType: 'address', name: 'account', type: 'address' }],
        name: 'MinterAdded',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: true, internalType: 'address', name: 'account', type: 'address' }],
        name: 'MinterRemoved',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: false, internalType: 'address', name: 'account', type: 'address' }],
        name: 'Paused',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: true, internalType: 'address', name: 'account', type: 'address' }],
        name: 'PauserAdded',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: true, internalType: 'address', name: 'account', type: 'address' }],
        name: 'PauserRemoved',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            { indexed: true, internalType: 'address', name: 'from', type: 'address' },
            { indexed: true, internalType: 'address', name: 'to', type: 'address' },
            { indexed: false, internalType: 'uint256', name: 'value', type: 'uint256' },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [{ indexed: false, internalType: 'address', name: 'account', type: 'address' }],
        name: 'Unpaused',
        type: 'event',
    },
    {
        constant: false,
        inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
        name: 'addMinter',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
        name: 'addPauser',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            { internalType: 'address', name: 'owner', type: 'address' },
            { internalType: 'address', name: 'spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'spender', type: 'address' },
            { internalType: 'uint256', name: 'value', type: 'uint256' },
        ],
        name: 'approve',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
        name: 'balanceOf',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
        name: 'burn',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'account', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
        ],
        name: 'burnFrom',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'decimals',
        outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
        name: 'isMinter',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
        name: 'isPauser',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'account', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
        ],
        name: 'mint',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'name',
        outputs: [{ internalType: 'string', name: '', type: 'string' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    { constant: false, inputs: [], name: 'pause', outputs: [], payable: false, stateMutability: 'nonpayable', type: 'function' },
    {
        constant: true,
        inputs: [],
        name: 'paused',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    { constant: false, inputs: [], name: 'renounceMinter', outputs: [], payable: false, stateMutability: 'nonpayable', type: 'function' },
    { constant: false, inputs: [], name: 'renouncePauser', outputs: [], payable: false, stateMutability: 'nonpayable', type: 'function' },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'recipient', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
        ],
        name: 'safeTransfer',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'recipient', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
            { internalType: 'bytes', name: 'data', type: 'bytes' },
        ],
        name: 'safeTransfer',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'sender', type: 'address' },
            { internalType: 'address', name: 'recipient', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'sender', type: 'address' },
            { internalType: 'address', name: 'recipient', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
            { internalType: 'bytes', name: 'data', type: 'bytes' },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [{ internalType: 'bytes4', name: 'interfaceId', type: 'bytes4' }],
        name: 'supportsInterface',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'symbol',
        outputs: [{ internalType: 'string', name: '', type: 'string' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'totalSupply',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [{ internalType: 'address', name: 'to', type: 'address' }, { internalType: 'uint256', name: 'value', type: 'uint256' }],
        name: 'transfer',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            { internalType: 'address', name: 'from', type: 'address' },
            { internalType: 'address', name: 'to', type: 'address' },
            { internalType: 'uint256', name: 'value', type: 'uint256' },
        ],
        name: 'transferFrom',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    { constant: false, inputs: [], name: 'unpause', outputs: [], payable: false, stateMutability: 'nonpayable', type: 'function' },
]

// KIP-7 token contract source code
// https://github.com/klaytn/klaytn-contracts/tree/master/contracts/token/KIP7/KIP7Token.sol
const kip7ByteCode =
    '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'

// KIP-17 token contract source code
// https://github.com/klaytn/caver-js/packages/caver-kct/src/KIP17Token.sol
// The ABI and bytecode below are built via the following command.
// solc --abi --bin --optimize --optimize-run 200 KIP17Token.sol
// compiler : v0.8.4+commit.c7e474f2
const kip17JsonInterface = [
    {
        inputs: [
            {
                internalType: 'string',
                name: 'name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: 'symbol',
                type: 'string',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'approved',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'ApprovalForAll',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'Paused',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'previousAdminRole',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'newAdminRole',
                type: 'bytes32',
            },
        ],
        name: 'RoleAdminChanged',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
        ],
        name: 'RoleGranted',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
        ],
        name: 'RoleRevoked',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'Unpaused',
        type: 'event',
    },
    {
        inputs: [],
        name: 'DEFAULT_ADMIN_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'MINTER_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'PAUSER_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'addMinter',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_account',
                type: 'address',
            },
        ],
        name: 'addPauser',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'burn',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'getApproved',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
        ],
        name: 'getRoleAdmin',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'getRoleMember',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
        ],
        name: 'getRoleMemberCount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'grantRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'hasRole',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
        ],
        name: 'isApprovedForAll',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'isMinter',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_account',
                type: 'address',
            },
        ],
        name: 'isPauser',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'mint',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                internalType: 'string',
                name: '_tokenURI',
                type: 'string',
            },
        ],
        name: 'mintWithTokenURI',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'ownerOf',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'pause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'paused',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceMinter',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renouncePauser',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'renounceRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'revokeRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                internalType: 'bytes',
                name: '_data',
                type: 'bytes',
            },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'setApprovalForAll',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes4',
                name: 'interfaceId',
                type: 'bytes4',
            },
        ],
        name: 'supportsInterface',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'tokenByIndex',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'tokenOfOwnerByIndex',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'tokenURI',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'unpause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
]
// KIP-17 token contract source code
// https://github.com/klaytn/caver-js/packages/caver-kct/src/KIP17Token.sol
const kip17ByteCode =
    '60806040523480156200001157600080fd5b5060405162003295380380620032958339810160408190526200003491620003ed565b8151829082906200004d90600090602085019062000294565b5080516200006390600190602084019062000294565b5050600c805460ff19169055506200007b33620000e8565b620000886000336200013a565b620000b47f3da911522173425ad77c4d334b95579cbb700456fef38bcc10dbcfbae688c2bc336200013a565b620000e07f879d41736c42986c020ca99e1e88db6c24a0b4923eb26f2115159827a9ccb72e336200013a565b5050620004a7565b600e80546001600160a01b038381166001600160a01b0319831681179093556040519116919082907f8be0079c531659141344cd1fd0a4f28419497f9722a3daafe3b4186f6b6457e090600090a35050565b6200015182826200017d60201b62000fe91760201c565b6000828152600b60209081526040909120620001789183906200106f62000222821b17901c565b505050565b6000828152600a602090815260408083206001600160a01b038516845290915290205460ff166200021e576000828152600a602090815260408083206001600160a01b03851684529091529020805460ff19166001179055620001dd3390565b6001600160a01b0316816001600160a01b0316837f2f8788117e7eff1d82e926ec794901d17c78024a50270940304540a733656f0d60405160405180910390a45b5050565b600062000239836001600160a01b03841662000242565b90505b92915050565b60008181526001830160205260408120546200028b575081546001818101845560008481526020808220909301849055845484825282860190935260409020919091556200023c565b5060006200023c565b828054620002a29062000454565b90600052602060002090601f016020900481019282620002c6576000855562000311565b82601f10620002e157805160ff191683800117855562000311565b8280016001018555821562000311579182015b8281111562000311578251825591602001919060010190620002f4565b506200031f92915062000323565b5090565b5b808211156200031f576000815560010162000324565b600082601f8301126200034b578081fd5b81516001600160401b038082111562000368576200036862000491565b604051601f8301601f19908116603f0116810190828211818310171562000393576200039362000491565b81604052838152602092508683858801011115620003af578485fd5b8491505b83821015620003d25785820183015181830184015290820190620003b3565b83821115620003e357848385830101525b9695505050505050565b6000806040838503121562000400578182fd5b82516001600160401b038082111562000417578384fd5b62000425868387016200033a565b935060208501519150808211156200043b578283fd5b506200044a858286016200033a565b9150509250929050565b600181811c908216806200046957607f821691505b602082108114156200048b57634e487b7160e01b600052602260045260246000fd5b50919050565b634e487b7160e01b600052604160045260246000fd5b612dde80620004b76000396000f3fe608060405234801561001057600080fd5b50600436106102535760003560e01c806370a0823111610146578063a217fddf116100c3578063ca15c87311610087578063ca15c873146104d7578063d5391393146104ea578063d547741f146104ff578063e63ab1e914610512578063e985e9c514610527578063f2fde38b1461056357600080fd5b8063a217fddf14610483578063a22cb4651461048b578063aa271e1a1461049e578063b88d4fde146104b1578063c87b56dd146104c457600080fd5b80639010d07c1161010a5780639010d07c1461043a57806391d148541461044d57806395d89b4114610460578063983b2d5614610468578063986502751461047b57600080fd5b806370a08231146103f3578063715018a61461040657806382dc1ec41461040e5780638456cb59146104215780638da5cb5b1461042957600080fd5b80633f4ba83a116101d45780634f6ccce7116101985780634f6ccce7146103aa57806350bb4e7f146103bd5780635c975abb146103d05780636352211e146103d85780636ef8d66d146103eb57600080fd5b80633f4ba83a1461035657806340c10f191461035e57806342842e0e1461037157806342966c681461038457806346fbf68e1461039757600080fd5b806323b872dd1161021b57806323b872dd146102e7578063248a9ca3146102fa5780632f2ff15d1461031d5780632f745c591461033057806336568abe1461034357600080fd5b806301ffc9a71461025857806306fdde0314610280578063081812fc14610295578063095ea7b3146102c057806318160ddd146102d5575b600080fd5b61026b6102663660046129f2565b610576565b60405190151581526020015b60405180910390f35b61028861060d565b6040516102779190612b37565b6102a86102a3366004612997565b61069f565b6040516001600160a01b039091168152602001610277565b6102d36102ce366004612906565b61072b565b005b6008545b604051908152602001610277565b6102d36102f5366004612818565b610837565b6102d9610308366004612997565b6000908152600a602052604090206001015490565b6102d361032b3660046129af565b610869565b6102d961033e366004612906565b61088e565b6102d36103513660046129af565b610923565b6102d36109a1565b61026b61036c366004612906565b610a27565b6102d361037f366004612818565b610a55565b6102d3610392366004612997565b610a70565b61026b6103a53660046127cc565b610aea565b6102d96103b8366004612997565b610b04565b61026b6103cb36600461292f565b610ba4565b61026b610bba565b6102a86103e6366004612997565b610bcd565b6102d3610c43565b6102d96104013660046127cc565b610c5b565b6102d3610ce0565b6102d361041c3660046127cc565b610d44565b6102d3610d67565b600e546001600160a01b03166102a8565b6102a86104483660046129d1565b610de9565b61026b61045b3660046129af565b610e08565b610288610e33565b6102d36104763660046127cc565b610e42565b6102d3610e65565b6102d9600081565b6102d36104993660046128cc565b610e7d565b61026b6104ac3660046127cc565b610e88565b6102d36104bf366004612853565b610ea2565b6102886104d2366004612997565b610eda565b6102d96104e5366004612997565b610ee5565b6102d9600080516020612d6983398151915281565b6102d361050d3660046129af565b610efc565b6102d9600080516020612d8983398151915281565b61026b6105353660046127e6565b6001600160a01b03918216600090815260056020908152604080832093909416825291909152205460ff1690565b6102d36105713660046127cc565b610f21565b60006001600160e01b03198216630852cd8d60e31b14806105a757506001600160e01b03198216634d5507ff60e01b145b806105c257506001600160e01b0319821663780e9d6360e01b145b806105dd57506001600160e01b03198216630755c1f160e51b145b806105f857506001600160e01b03198216637d613fa360e11b145b80610607575061060782611084565b92915050565b60606000805461061c90612ca6565b80601f016020809104026020016040519081016040528092919081815260200182805461064890612ca6565b80156106955780601f1061066a57610100808354040283529160200191610695565b820191906000526020600020905b81548152906001019060200180831161067857829003601f168201915b5050505050905090565b60006106aa826110d4565b61070f5760405162461bcd60e51b815260206004820152602b60248201527f4b495031373a20617070726f76656420717565727920666f72206e6f6e65786960448201526a39ba32b73a103a37b5b2b760a91b60648201526084015b60405180910390fd5b506000908152600460205260409020546001600160a01b031690565b600061073682610bcd565b9050806001600160a01b0316836001600160a01b0316141561079a5760405162461bcd60e51b815260206004820181905260248201527f4b495031373a20617070726f76616c20746f2063757272656e74206f776e65726044820152606401610706565b336001600160a01b03821614806107b657506107b68133610535565b6108285760405162461bcd60e51b815260206004820152603760248201527f4b495031373a20617070726f76652063616c6c6572206973206e6f74206f776e60448201527f6572206e6f7220617070726f76656420666f7220616c6c0000000000000000006064820152608401610706565b61083283836110f1565b505050565b610842335b8261115f565b61085e5760405162461bcd60e51b815260040161070690612bb1565b610832838383611248565b6000828152600a6020526040902060010154610884816113ed565b61083283836113f7565b600061089983610c5b565b82106108fa5760405162461bcd60e51b815260206004820152602a60248201527f4b49503137456e756d657261626c653a206f776e657220696e646578206f7574604482015269206f6620626f756e647360b01b6064820152608401610706565b506001600160a01b03919091166000908152600660209081526040808320938352929052205490565b6001600160a01b03811633146109935760405162461bcd60e51b815260206004820152602f60248201527f416363657373436f6e74726f6c3a2063616e206f6e6c792072656e6f756e636560448201526e103937b632b9903337b91039b2b63360891b6064820152608401610706565b61099d8282611419565b5050565b6109b9600080516020612d8983398151915233610e08565b610a1d5760405162461bcd60e51b815260206004820152602f60248201527f4b495031375061757361626c653a206d7573742068617665207061757365722060448201526e726f6c6520746f20756e706175736560881b6064820152608401610706565b610a2561143b565b565b6000600080516020612d69833981519152610a41816113ed565b610a4b84846114d0565b5060019392505050565b61083283838360405180602001604052806000815250610ea2565b610a793361083c565b610ade5760405162461bcd60e51b815260206004820152603060248201527f4552433732314275726e61626c653a2063616c6c6572206973206e6f74206f7760448201526f1b995c881b9bdc88185c1c1c9bdd995960821b6064820152608401610706565b610ae7816114ea565b50565b6000610607600080516020612d8983398151915283610e08565b6000610b0f60085490565b8210610b715760405162461bcd60e51b815260206004820152602b60248201527f4b49503137456e756d657261626c653a20676c6f62616c20696e646578206f7560448201526a74206f6620626f756e647360a81b6064820152608401610706565b60088281548110610b9257634e487b7160e01b600052603260045260246000fd5b90600052602060002001549050919050565b6000610bb084846114d0565b610a4b83836114f3565b6000610bc8600c5460ff1690565b905090565b6000818152600260205260408120546001600160a01b0316806106075760405162461bcd60e51b815260206004820152602860248201527f4b495031373a206f776e657220717565727920666f72206e6f6e657869737465604482015267373a103a37b5b2b760c11b6064820152608401610706565b610a25600080516020612d8983398151915233610923565b60006001600160a01b038216610cc45760405162461bcd60e51b815260206004820152602860248201527f4b495031373a2061646472657373207a65726f206973206e6f7420612076616c60448201526734b21037bbb732b960c11b6064820152608401610706565b506001600160a01b031660009081526003602052604090205490565b600e546001600160a01b03163314610d3a5760405162461bcd60e51b815260206004820181905260248201527f4f776e61626c653a2063616c6c6572206973206e6f7420746865206f776e65726044820152606401610706565b610a25600061157d565b6000610d4f816113ed565b61099d600080516020612d8983398151915283610869565b610d7f600080516020612d8983398151915233610e08565b610de15760405162461bcd60e51b815260206004820152602d60248201527f4b495031375061757361626c653a206d7573742068617665207061757365722060448201526c726f6c6520746f20706175736560981b6064820152608401610706565b610a256115cf565b6000828152600b60205260408120610e01908361164c565b9392505050565b6000918252600a602090815260408084206001600160a01b0393909316845291905290205460ff1690565b60606001805461061c90612ca6565b6000610e4d816113ed565b61099d600080516020612d6983398151915283610869565b610a25600080516020612d6983398151915233610923565b61099d338383611658565b6000610607600080516020612d6983398151915283610e08565b610eac338361115f565b610ec85760405162461bcd60e51b815260040161070690612bb1565b610ed484848484611727565b50505050565b60606106078261176c565b6000818152600b60205260408120610607906118da565b6000828152600a6020526040902060010154610f17816113ed565b6108328383611419565b600e546001600160a01b03163314610f7b5760405162461bcd60e51b815260206004820181905260248201527f4f776e61626c653a2063616c6c6572206973206e6f7420746865206f776e65726044820152606401610706565b6001600160a01b038116610fe05760405162461bcd60e51b815260206004820152602660248201527f4f776e61626c653a206e6577206f776e657220697320746865207a65726f206160448201526564647265737360d01b6064820152608401610706565b610ae78161157d565b610ff38282610e08565b61099d576000828152600a602090815260408083206001600160a01b03851684529091529020805460ff1916600117905561102b3390565b6001600160a01b0316816001600160a01b0316837f2f8788117e7eff1d82e926ec794901d17c78024a50270940304540a733656f0d60405160405180910390a45050565b6000610e01836001600160a01b0384166118e4565b60006001600160e01b031982166380ac58cd60e01b14806110b557506001600160e01b03198216635b5e139f60e01b145b8061060757506301ffc9a760e01b6001600160e01b0319831614610607565b6000908152600260205260409020546001600160a01b0316151590565b600081815260046020526040902080546001600160a01b0319166001600160a01b038416908117909155819061112682610bcd565b6001600160a01b03167f8c5be1e5ebec7d5bd14f71427d1e84f3dd0314c0f7b2291e5b200ac8c7c3b92560405160405180910390a45050565b600061116a826110d4565b6111ca5760405162461bcd60e51b815260206004820152602b60248201527f4b495031373a206f70657261746f7220717565727920666f72206e6f6e65786960448201526a39ba32b73a103a37b5b2b760a91b6064820152608401610706565b60006111d583610bcd565b9050806001600160a01b0316846001600160a01b0316148061121c57506001600160a01b0380821660009081526005602090815260408083209388168352929052205460ff165b806112405750836001600160a01b03166112358461069f565b6001600160a01b0316145b949350505050565b826001600160a01b031661125b82610bcd565b6001600160a01b0316146112bd5760405162461bcd60e51b8152602060048201526024808201527f4b495031373a207472616e736665722066726f6d20696e636f7272656374206f6044820152633bb732b960e11b6064820152608401610706565b6001600160a01b03821661131f5760405162461bcd60e51b815260206004820152602360248201527f4b495031373a207472616e7366657220746f20746865207a65726f206164647260448201526265737360e81b6064820152608401610706565b61132a838383611933565b6113356000826110f1565b6001600160a01b038316600090815260036020526040812080546001929061135e908490612c4c565b90915550506001600160a01b038216600090815260036020526040812080546001929061138c908490612c01565b909155505060008181526002602052604080822080546001600160a01b0319166001600160a01b0386811691821790925591518493918716917fddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef91a4505050565b610ae7813361193e565b6114018282610fe9565b6000828152600b60205260409020610832908261106f565b61142382826119a2565b6000828152600b602052604090206108329082611a09565b611443610bba565b6114865760405162461bcd60e51b815260206004820152601460248201527314185d5cd8589b194e881b9bdd081c185d5cd95960621b6044820152606401610706565b600c805460ff191690557f5db9ee0a495bf2e6ff9c91a7834c1ba4fdd244a5e8aa4e537bd38aeae4b073aa335b6040516001600160a01b03909116815260200160405180910390a1565b61099d828260405180602001604052806000815250611a1e565b610ae781611a64565b6114fc826110d4565b61155e5760405162461bcd60e51b815260206004820152602d60248201527f4b4950313755524953746f726167653a2055524920736574206f66206e6f6e6560448201526c3c34b9ba32b73a103a37b5b2b760991b6064820152608401610706565b6000828152600d6020908152604090912082516108329284019061266b565b600e80546001600160a01b038381166001600160a01b0319831681179093556040519116919082907f8be0079c531659141344cd1fd0a4f28419497f9722a3daafe3b4186f6b6457e090600090a35050565b6115d7610bba565b156116175760405162461bcd60e51b815260206004820152601060248201526f14185d5cd8589b194e881c185d5cd95960821b6044820152606401610706565b600c805460ff191660011790557f62e78cea01bee320cd4e420270b5ea74000d11b0c9f74754ebdbfc544b05a2586114b33390565b6000610e018383611aa4565b816001600160a01b0316836001600160a01b031614156116ba5760405162461bcd60e51b815260206004820152601860248201527f4b495031373a20617070726f766520746f2063616c6c657200000000000000006044820152606401610706565b6001600160a01b03838116600081815260056020908152604080832094871680845294825291829020805460ff191686151590811790915591519182527f17307eab39ab6107e8899845ad3d59bd9653f200f220920489ca2b5937696c31910160405180910390a3505050565b611732848484611248565b61173e84848484611adc565b80611750575061175084848484611bdb565b610ed45760405162461bcd60e51b815260040161070690612b4a565b6060611777826110d4565b6117dc5760405162461bcd60e51b815260206004820152603060248201527f4b4950313755524953746f726167653a2055524920717565727920666f72206e60448201526f37b732bc34b9ba32b73a103a37b5b2b760811b6064820152608401610706565b6000828152600d6020526040812080546117f590612ca6565b80601f016020809104026020016040519081016040528092919081815260200182805461182190612ca6565b801561186e5780601f106118435761010080835404028352916020019161186e565b820191906000526020600020905b81548152906001019060200180831161185157829003601f168201915b50505050509050600061188c60408051602081019091526000815290565b905080516000141561189f575092915050565b8151156118d15780826040516020016118b9929190612a56565b60405160208183030381529060405292505050919050565b61124084611cb1565b6000610607825490565b600081815260018301602052604081205461192b57508154600181810184556000848152602080822090930184905584548482528286019093526040902091909155610607565b506000610607565b610832838383611d87565b6119488282610e08565b61099d57611960816001600160a01b03166014611dfa565b61196b836020611dfa565b60405160200161197c929190612a85565b60408051601f198184030181529082905262461bcd60e51b825261070691600401612b37565b6119ac8282610e08565b1561099d576000828152600a602090815260408083206001600160a01b0385168085529252808320805460ff1916905551339285917ff6391f5c32d9c69d2a47ea670b442974b53935d1edc7fd64eb21e047a839171b9190a45050565b6000610e01836001600160a01b038416611fdc565b611a2883836120f9565b611a356000848484611adc565b80611a485750611a486000848484611bdb565b6108325760405162461bcd60e51b815260040161070690612b4a565b611a6d81612238565b6000818152600d602052604090208054611a8690612ca6565b159050610ae7576000818152600d60205260408120610ae7916126ef565b6000826000018281548110611ac957634e487b7160e01b600052603260045260246000fd5b9060005260206000200154905092915050565b60006001600160a01b0384163b15611bd057604051636745782b60e01b81526001600160a01b03851690636745782b90611b20903390899088908890600401612afa565b602060405180830381600087803b158015611b3a57600080fd5b505af1925050508015611b6a575060408051601f3d908101601f19168201909252611b6791810190612a0e565b60015b611bb6573d808015611b98576040519150601f19603f3d011682016040523d82523d6000602084013e611b9d565b606091505b508051611bae576000915050611240565b805181602001fd5b6001600160e01b031916636745782b60e01b149050611240565b506001949350505050565b60006001600160a01b0384163b15611bd057604051630a85bd0160e11b81526001600160a01b0385169063150b7a0290611c1f903390899088908890600401612afa565b602060405180830381600087803b158015611c3957600080fd5b505af1925050508015611c69575060408051601f3d908101601f19168201909252611c6691810190612a0e565b60015b611c97573d808015611b98576040519150601f19603f3d011682016040523d82523d6000602084013e611b9d565b6001600160e01b031916630a85bd0160e11b149050611240565b6060611cbc826110d4565b611d1f5760405162461bcd60e51b815260206004820152602e60248201527f4b495031374d657461646174613a2055524920717565727920666f72206e6f6e60448201526d32bc34b9ba32b73a103a37b5b2b760911b6064820152608401610706565b6000611d3660408051602081019091526000815290565b90506000815111611d565760405180602001604052806000815250610e01565b80611d60846122df565b604051602001611d71929190612a56565b6040516020818303038152906040529392505050565b611d928383836123f9565b611d9a610bba565b156108325760405162461bcd60e51b815260206004820152602a60248201527f4b495031375061757361626c653a20746f6b656e207472616e736665722077686044820152691a5b19481c185d5cd95960b21b6064820152608401610706565b60606000611e09836002612c2d565b611e14906002612c01565b67ffffffffffffffff811115611e3a57634e487b7160e01b600052604160045260246000fd5b6040519080825280601f01601f191660200182016040528015611e64576020820181803683370190505b509050600360fc1b81600081518110611e8d57634e487b7160e01b600052603260045260246000fd5b60200101906001600160f81b031916908160001a905350600f60fb1b81600181518110611eca57634e487b7160e01b600052603260045260246000fd5b60200101906001600160f81b031916908160001a9053506000611eee846002612c2d565b611ef9906001612c01565b90505b6001811115611f8d576f181899199a1a9b1b9c1cb0b131b232b360811b85600f1660108110611f3b57634e487b7160e01b600052603260045260246000fd5b1a60f81b828281518110611f5f57634e487b7160e01b600052603260045260246000fd5b60200101906001600160f81b031916908160001a90535060049490941c93611f8681612c8f565b9050611efc565b508315610e015760405162461bcd60e51b815260206004820181905260248201527f537472696e67733a20686578206c656e67746820696e73756666696369656e746044820152606401610706565b600081815260018301602052604081205480156120ef576000612000600183612c4c565b855490915060009061201490600190612c4c565b905081811461209557600086600001828154811061204257634e487b7160e01b600052603260045260246000fd5b906000526020600020015490508087600001848154811061207357634e487b7160e01b600052603260045260246000fd5b6000918252602080832090910192909255918252600188019052604090208390555b85548690806120b457634e487b7160e01b600052603160045260246000fd5b600190038181906000526020600020016000905590558560010160008681526020019081526020016000206000905560019350505050610607565b6000915050610607565b6001600160a01b03821661214f5760405162461bcd60e51b815260206004820152601f60248201527f4b495031373a206d696e7420746f20746865207a65726f2061646472657373006044820152606401610706565b612158816110d4565b156121a55760405162461bcd60e51b815260206004820152601b60248201527f4b495031373a20746f6b656e20616c7265616479206d696e74656400000000006044820152606401610706565b6121b160008383611933565b6001600160a01b03821660009081526003602052604081208054600192906121da908490612c01565b909155505060008181526002602052604080822080546001600160a01b0319166001600160a01b03861690811790915590518392907fddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef908290a45050565b600061224382610bcd565b905061225181600084611933565b61225c6000836110f1565b6001600160a01b0381166000908152600360205260408120805460019290612285908490612c4c565b909155505060008281526002602052604080822080546001600160a01b0319169055518391906001600160a01b038416907fddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef908390a45050565b6060816123035750506040805180820190915260018152600360fc1b602082015290565b8160005b811561232d578061231781612ce1565b91506123269050600a83612c19565b9150612307565b60008167ffffffffffffffff81111561235657634e487b7160e01b600052604160045260246000fd5b6040519080825280601f01601f191660200182016040528015612380576020820181803683370190505b5090505b841561124057612395600183612c4c565b91506123a2600a86612cfc565b6123ad906030612c01565b60f81b8183815181106123d057634e487b7160e01b600052603260045260246000fd5b60200101906001600160f81b031916908160001a9053506123f2600a86612c19565b9450612384565b6001600160a01b0383166124545761244f81600880546000838152600960205260408120829055600182018355919091527ff3f7a9fe364faab93b216da50a3214154f22a0a2b415b23a84c8169e8b636ee30155565b612477565b816001600160a01b0316836001600160a01b0316146124775761247783826124b1565b6001600160a01b03821661248e576108328161254e565b826001600160a01b0316826001600160a01b031614610832576108328282612627565b600060016124be84610c5b565b6124c89190612c4c565b60008381526007602052604090205490915080821461251b576001600160a01b03841660009081526006602090815260408083208584528252808320548484528184208190558352600790915290208190555b5060009182526007602090815260408084208490556001600160a01b039094168352600681528383209183525290812055565b60085460009061256090600190612c4c565b6000838152600960205260408120546008805493945090928490811061259657634e487b7160e01b600052603260045260246000fd5b9060005260206000200154905080600883815481106125c557634e487b7160e01b600052603260045260246000fd5b600091825260208083209091019290925582815260099091526040808220849055858252812055600880548061260b57634e487b7160e01b600052603160045260246000fd5b6001900381819060005260206000200160009055905550505050565b600061263283610c5b565b6001600160a01b039093166000908152600660209081526040808320868452825280832085905593825260079052919091209190915550565b82805461267790612ca6565b90600052602060002090601f01602090048101928261269957600085556126df565b82601f106126b257805160ff19168380011785556126df565b828001600101855582156126df579182015b828111156126df5782518255916020019190600101906126c4565b506126eb929150612725565b5090565b5080546126fb90612ca6565b6000825580601f1061270b575050565b601f016020900490600052602060002090810190610ae791905b5b808211156126eb5760008155600101612726565b600067ffffffffffffffff8084111561275557612755612d3c565b604051601f8501601f19908116603f0116810190828211818310171561277d5761277d612d3c565b8160405280935085815286868601111561279657600080fd5b858560208301376000602087830101525050509392505050565b80356001600160a01b03811681146127c757600080fd5b919050565b6000602082840312156127dd578081fd5b610e01826127b0565b600080604083850312156127f8578081fd5b612801836127b0565b915061280f602084016127b0565b90509250929050565b60008060006060848603121561282c578081fd5b612835846127b0565b9250612843602085016127b0565b9150604084013590509250925092565b60008060008060808587031215612868578081fd5b612871856127b0565b935061287f602086016127b0565b925060408501359150606085013567ffffffffffffffff8111156128a1578182fd5b8501601f810187136128b1578182fd5b6128c08782356020840161273a565b91505092959194509250565b600080604083850312156128de578182fd5b6128e7836127b0565b9150602083013580151581146128fb578182fd5b809150509250929050565b60008060408385031215612918578182fd5b612921836127b0565b946020939093013593505050565b600080600060608486031215612943578283fd5b61294c846127b0565b925060208401359150604084013567ffffffffffffffff81111561296e578182fd5b8401601f8101861361297e578182fd5b61298d8682356020840161273a565b9150509250925092565b6000602082840312156129a8578081fd5b5035919050565b600080604083850312156129c1578182fd5b8235915061280f602084016127b0565b600080604083850312156129e3578182fd5b50508035926020909101359150565b600060208284031215612a03578081fd5b8135610e0181612d52565b600060208284031215612a1f578081fd5b8151610e0181612d52565b60008151808452612a42816020860160208601612c63565b601f01601f19169290920160200192915050565b60008351612a68818460208801612c63565b835190830190612a7c818360208801612c63565b01949350505050565b7f416363657373436f6e74726f6c3a206163636f756e7420000000000000000000815260008351612abd816017850160208801612c63565b7001034b99036b4b9b9b4b733903937b6329607d1b6017918401918201528351612aee816028840160208801612c63565b01602801949350505050565b6001600160a01b0385811682528416602082015260408101839052608060608201819052600090612b2d90830184612a2a565b9695505050505050565b602081526000610e016020830184612a2a565b60208082526041908201527f4b495031373a207472616e7366657220746f206e6f6e20494b4950313752656360408201527f65697665722f49455243373231526563656976657220696d706c656d656e74656060820152603960f91b608082015260a00190565b60208082526030908201527f4b495031373a207472616e736665722063616c6c6572206973206e6f74206f7760408201526f1b995c881b9bdc88185c1c1c9bdd995960821b606082015260800190565b60008219821115612c1457612c14612d10565b500190565b600082612c2857612c28612d26565b500490565b6000816000190483118215151615612c4757612c47612d10565b500290565b600082821015612c5e57612c5e612d10565b500390565b60005b83811015612c7e578181015183820152602001612c66565b83811115610ed45750506000910152565b600081612c9e57612c9e612d10565b506000190190565b600181811c90821680612cba57607f821691505b60208210811415612cdb57634e487b7160e01b600052602260045260246000fd5b50919050565b6000600019821415612cf557612cf5612d10565b5060010190565b600082612d0b57612d0b612d26565b500690565b634e487b7160e01b600052601160045260246000fd5b634e487b7160e01b600052601260045260246000fd5b634e487b7160e01b600052604160045260246000fd5b6001600160e01b031981168114610ae757600080fdfe879d41736c42986c020ca99e1e88db6c24a0b4923eb26f2115159827a9ccb72e3da911522173425ad77c4d334b95579cbb700456fef38bcc10dbcfbae688c2bca26469706673582212206b80aba2b777b49f620da765c82777cde36a37231d42a44c1b8100fc86c9c53564736f6c63430008040033'
// KIP-37 token contract source code
// https://github.com/klaytn/caver-js/packages/caver-kct/src/KIP37Token.sol
// The ABI and bytecode below are built via the following command.
// solc --abi --bin --optimize --optimize-run 200 KIP37Token.sol
// compiler : v0.8.4+commit.c7e474f2
const kip37JsonInterface = [
    {
        inputs: [
            {
                internalType: 'string',
                name: 'uri_',
                type: 'string',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'ApprovalForAll',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'Paused',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'previousAdminRole',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'newAdminRole',
                type: 'bytes32',
            },
        ],
        name: 'RoleAdminChanged',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
        ],
        name: 'RoleGranted',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
        ],
        name: 'RoleRevoked',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'TokenPaused',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'TokenUnpaused',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256[]',
                name: 'ids',
                type: 'uint256[]',
            },
            {
                indexed: false,
                internalType: 'uint256[]',
                name: 'amounts',
                type: 'uint256[]',
            },
        ],
        name: 'TransferBatch',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'TransferSingle',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'string',
                name: 'value',
                type: 'string',
            },
            {
                indexed: true,
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'URI',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'Unpaused',
        type: 'event',
    },
    {
        inputs: [],
        name: 'DEFAULT_ADMIN_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'MINTER_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'PAUSER_ROLE',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'addMinter',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_account',
                type: 'address',
            },
        ],
        name: 'addPauser',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address[]',
                name: 'owners',
                type: 'address[]',
            },
            {
                internalType: 'uint256[]',
                name: 'ids',
                type: 'uint256[]',
            },
        ],
        name: 'balanceOfBatch',
        outputs: [
            {
                internalType: 'uint256[]',
                name: '',
                type: 'uint256[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'burn',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                internalType: 'uint256[]',
                name: 'ids',
                type: 'uint256[]',
            },
            {
                internalType: 'uint256[]',
                name: 'amounts',
                type: 'uint256[]',
            },
        ],
        name: 'burnBatch',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'initialSupply',
                type: 'uint256',
            },
            {
                internalType: 'string',
                name: 'uri_',
                type: 'string',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        name: 'creators',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'exists',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
        ],
        name: 'getRoleAdmin',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'getRoleMember',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
        ],
        name: 'getRoleMemberCount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'grantRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'hasRole',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
        ],
        name: 'isApprovedForAll',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'isMinter',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_account',
                type: 'address',
            },
        ],
        name: 'isPauser',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'mint',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address[]',
                name: 'toList',
                type: 'address[]',
            },
            {
                internalType: 'uint256[]',
                name: 'amounts',
                type: 'uint256[]',
            },
        ],
        name: 'mint',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256[]',
                name: 'ids',
                type: 'uint256[]',
            },
            {
                internalType: 'uint256[]',
                name: 'amounts',
                type: 'uint256[]',
            },
        ],
        name: 'mintBatch',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'pause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'pause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'paused',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'paused',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceMinter',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renouncePauser',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'renounceRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes32',
                name: 'role',
                type: 'bytes32',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'revokeRole',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256[]',
                name: 'ids',
                type: 'uint256[]',
            },
            {
                internalType: 'uint256[]',
                name: 'amounts',
                type: 'uint256[]',
            },
            {
                internalType: 'bytes',
                name: 'data',
                type: 'bytes',
            },
        ],
        name: 'safeBatchTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
            {
                internalType: 'bytes',
                name: 'data',
                type: 'bytes',
            },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'setApprovalForAll',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes4',
                name: 'interfaceId',
                type: 'bytes4',
            },
        ],
        name: 'supportsInterface',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'unpause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
        ],
        name: 'unpause',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'uri',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
]

// KIP-37 token contract source code
// https://github.com/klaytn/caver-js/packages/caver-kct/src/KIP37Token.sol
const kip37ByteCode =
    '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'

const kip13JsonInterface = [
    {
        constant: true,
        inputs: [{ name: 'interfaceId', type: 'bytes4' }],
        name: 'supportsInterface',
        outputs: [{ name: '', type: 'bool' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
]

const interfaceIds = {
    preCondition: {
        true: '0x01ffc9a7',
        false: '0xffffffff',
    },
    kip7: {
        IKIP7: '0x65787371',
        IKIP7Metadata: '0xa219a025',
        IKIP7Mintable: '0xeab83e20',
        IKIP7Burnable: '0x3b5a0bf8',
        IKIP7Pausable: '0x4d5507ff',
    },
    kip17: {
        IKIP17: '0x80ac58cd',
        IKIP17Metadata: '0x5b5e139f',
        IKIP17Enumerable: '0x780e9d63',
        IKIP17Mintable: '0xeab83e20',
        IKIP17MetadataMintable: '0xfac27f46',
        IKIP17Burnable: '0x42966c68',
        IKIP17Pausable: '0x4d5507ff',
    },
    kip37: {
        IKIP37: '0x6433ca1f',
        IKIP37Metadata: '0x0e89341c',
        IKIP37Mintable: '0xdfd9d9ec',
        IKIP37Burnable: '0x9e094e9e',
        IKIP37Pausable: '0x0e8ffdb7',
    },
}

module.exports = {
    interfaceIds,
    kip7JsonInterface,
    kip7ByteCode,
    determineSendParams,
    validateDeployParameterForKIP7,
    validateDeployParameterForKIP17,
    validateDeployParameterForKIP37,
    formatParamForUint256,
    kip17JsonInterface,
    kip17ByteCode,
    kip37JsonInterface,
    kip37ByteCode,
    kip13JsonInterface,
}
